import React, { useEffect, useMemo, useRef } from 'react';
import mrLiquidDemoHtml from '../../../../../../新規追加ミニゲーム/実装済み/mr_liquid_demo.html?raw';
import { useManualSubmit } from '../hooks';

const VARIANT_META = {
  film_search: {
    demoGame: 'exploration',
    number: 'N° 01',
    ability: '液膜探索',
    topTitle: 'N° 01  液膜探索  TRACE'
  },
  mirror_align: {
    demoGame: 'mirror',
    number: 'N° 02',
    ability: '水鏡合わせ',
    topTitle: 'N° 02  水鏡合わせ  MIRROR'
  },
  water_battle: {
    demoGame: 'battle',
    number: 'N° 03',
    ability: '放水バトル',
    topTitle: 'N° 03  放水バトル  SUBDUE'
  },
  fire_line: {
    demoGame: 'firewall',
    number: 'N° 04',
    ability: '消火ライン',
    topTitle: 'N° 04  消火ライン  QUENCH'
  }
};

const DEFAULT_MAP_SPOTS = [
  { id: 'gate', label: '商店街入口', keyword: '水守屋', ability: '液膜探索' },
  { id: 'well', label: '井戸', keyword: '水鏡', ability: '水鏡合わせ' },
  { id: 'lantern', label: '灯籠路地', keyword: '火鎮め', ability: '放水バトル' },
  { id: 'sign', label: '看板裏', keyword: '墨線', ability: '液体トレース' },
  { id: 'shrine', label: '祠と蔵', keyword: '水守の祠', ability: '複合液術' }
];

const Template19MrLiquid = ({
  hostSubmit,
  hostConfig,
  registryId = 'minigame_mr_liquid_19',
  templateId = 19
}) => {
  const iframeRef = useRef(null);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const resolved = useMemo(() => resolveConfig(hostConfig), [hostConfig]);
  const srcDoc = useMemo(() => buildMrLiquidSrcDoc(resolved), [resolved]);

  useEffect(() => {
    resetSubmission();
  }, [resetSubmission, resolved.variant, resolved.title, resolved.abilityLabel, resolved.clueKey]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (iframeRef.current?.contentWindow && event.source !== iframeRef.current.contentWindow) {
        return;
      }
      const data = event.data;
      if (!data || data.type !== 'mr-liquid-result') {
        return;
      }

      submit(data.status === 'failure' ? 'failure' : 'success', {
        score: Number.isFinite(data.score) ? data.score : 100,
        variant: resolved.variant,
        ability: resolved.abilityLabel,
        clueKey: resolved.clueKey,
        mapSpotId: resolved.map.spots[resolved.map.currentIndex]?.id,
        source: 'mr_liquid_demo.html'
      });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [resolved, submit]);

  return (
    <div style={styles.shell}>
      <iframe
        key={`${resolved.variant}:${resolved.title}:${resolved.abilityLabel}`}
        ref={iframeRef}
        title={`Mr.リキッド ${resolved.abilityLabel}`}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        style={styles.frame}
      />
    </div>
  );
};

function resolveConfig(config) {
  const variant = config?.variant && VARIANT_META[config.variant] ? config.variant : 'film_search';
  const meta = VARIANT_META[variant];
  const map = {
    title: config?.map?.title ?? '水守商店街 簡易マップ',
    currentIndex: Number.isFinite(config?.map?.currentIndex) ? config.map.currentIndex : 0,
    spots: Array.isArray(config?.map?.spots) && config.map.spots.length > 0 ? config.map.spots : DEFAULT_MAP_SPOTS
  };

  return {
    variant,
    title: config?.title ?? meta.topTitle,
    abilityLabel: config?.abilityLabel ?? meta.ability,
    clueKey: config?.clueKey,
    successText: config?.successText ?? '手がかりが液面に浮かび上がった。',
    map
  };
}

function buildMrLiquidSrcDoc(config) {
  const meta = VARIANT_META[config.variant] ?? VARIANT_META.film_search;
  const bridgeConfig = JSON.stringify({
    game: meta.demoGame,
    variant: config.variant,
    number: meta.number,
    abilityLabel: meta.ability,
    topTitle: meta.topTitle,
    successText: config.successText
  }).replace(/</g, '\\u003c');

  const headPatch = `
<style>
  html, body { width: 100%; height: 100%; background: transparent; }
  body { overflow: hidden; }
  #app { width: 100%; height: 100%; }
  .stage {
    width: min(900px, 100vw) !important;
    height: min(600px, 100vh) !important;
    max-width: 100vw;
    max-height: 100vh;
  }
  .opening,
  #menu,
  #finale,
  .topbar .back {
    display: none !important;
  }
  .clear-overlay button[data-mr-liquid-submit] {
    border-color: var(--water);
    color: #dff8ff;
  }
  @media (max-width: 480px) {
    .topbar { height: 38px; padding: 0 12px; }
    .topbar .title { font-size: 10px; letter-spacing: 0.12em; }
    .hud { top: 44px; left: 10px; right: 10px; }
    .hud-left, .hud-right { padding: 6px 9px; font-size: 10px; }
    .liquid-line { bottom: 10px; max-width: calc(100% - 22px); padding: 9px 12px; font-size: 11px; }
    .clear-overlay h2 { font-size: 26px; letter-spacing: 0.12em; text-align: center; }
    .ending-line { padding: 0 20px; font-size: 12px; }
  }
</style>`;

  const bodyPatch = `
<script>
(function () {
  var scenario = ${bridgeConfig};
  var clearOverlayByGame = {
    exploration: 'explClear',
    mirror: 'mirrorClear',
    battle: 'battleClear',
    firewall: 'firewallClear'
  };

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function patchStandaloneDemo() {
    var opening = document.getElementById('opening');
    if (opening) {
      opening.classList.add('hide');
      opening.style.display = 'none';
    }
    var menu = document.getElementById('menu');
    if (menu) {
      menu.classList.remove('active');
      menu.style.display = 'none';
    }
    var back = document.getElementById('backBtn');
    if (back) {
      back.classList.add('hidden');
      back.style.display = 'none';
    }

    try {
      if (typeof TITLES !== 'undefined') {
        TITLES[scenario.game] = scenario.topTitle;
      }
      if (typeof GAME_META !== 'undefined' && GAME_META[scenario.game]) {
        GAME_META[scenario.game].name = scenario.abilityLabel;
      }
    } catch (error) {
      parent.postMessage({ type: 'mr-liquid-error', message: String(error) }, '*');
    }
  }

  function patchClearButtons() {
    Object.keys(clearOverlayByGame).forEach(function (game) {
      var overlay = document.getElementById(clearOverlayByGame[game]);
      if (!overlay) return;
      var row = overlay.querySelector('div[style*="margin-top"]') || overlay;
      var buttons = Array.prototype.slice.call(row.querySelectorAll('button'));
      if (buttons[0]) {
        buttons[0].textContent = 'もう一度';
        buttons[0].onclick = function () {
          if (typeof restartGame === 'function') restartGame(game);
        };
      }

      var submitButton = buttons[1] || document.createElement('button');
      submitButton.textContent = '結果を送る';
      submitButton.setAttribute('data-mr-liquid-submit', 'true');
      submitButton.onclick = function () {
        sendResult(game);
      };
      if (!buttons[1]) {
        row.appendChild(submitButton);
      }
    });
  }

  function installTouchBridge() {
    ['explorationCanvas', 'mirrorCanvas', 'battleCanvas', 'firewallCanvas'].forEach(function (id) {
      var canvas = document.getElementById(id);
      if (!canvas) return;
      var lastTouch = null;

      function mouse(type, touch, target) {
        if (!touch) return;
        var event = new MouseEvent(type, {
          bubbles: true,
          cancelable: true,
          clientX: touch.clientX,
          clientY: touch.clientY,
          screenX: touch.screenX,
          screenY: touch.screenY
        });
        (target || canvas).dispatchEvent(event);
      }

      canvas.addEventListener('touchstart', function (event) {
        lastTouch = event.changedTouches[0];
        mouse('mousedown', lastTouch);
        event.preventDefault();
      }, { passive: false });
      canvas.addEventListener('touchmove', function (event) {
        lastTouch = event.changedTouches[0];
        mouse('mousemove', lastTouch);
        event.preventDefault();
      }, { passive: false });
      canvas.addEventListener('touchend', function (event) {
        lastTouch = event.changedTouches[0] || lastTouch;
        mouse('mouseup', lastTouch, window);
        event.preventDefault();
      }, { passive: false });
      canvas.addEventListener('touchcancel', function (event) {
        lastTouch = event.changedTouches[0] || lastTouch;
        mouse('mouseup', lastTouch, window);
        event.preventDefault();
      }, { passive: false });
    });
  }

  function isCleared(game) {
    try {
      return typeof gameCleared !== 'undefined' && gameCleared && gameCleared[game] === true;
    } catch (_) {
      return false;
    }
  }

  function sendResult(game) {
    if (!isCleared(game)) {
      if (typeof showLine === 'function') {
        var lineId = game === 'exploration' ? 'explLine' :
          game === 'mirror' ? 'mirrorLine' :
          game === 'battle' ? 'battleLine' : 'firewallLine';
        showLine(lineId, 'まだ終わっておらぬよ、ムッシュ。もう一度整えようじゃないか', 2800);
      }
      if (typeof restartGame === 'function') {
        restartGame(game);
      }
      return;
    }

    parent.postMessage({
      type: 'mr-liquid-result',
      status: 'success',
      score: 100,
      game: game,
      variant: scenario.variant,
      ability: scenario.abilityLabel,
      source: 'mr_liquid_demo.html'
    }, '*');
  }

  ready(function () {
    patchStandaloneDemo();
    patchClearButtons();
    installTouchBridge();
    setTimeout(function () {
      if (typeof startGame === 'function') {
        startGame(scenario.game);
      }
    }, 80);
  });
}());
</script>`;

  return mrLiquidDemoHtml
    .replace('</head>', `${headPatch}\n</head>`)
    .replace('</body>', `${bodyPatch}\n</body>`);
}

const styles = {
  shell: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    background: 'radial-gradient(ellipse at 30% 20%, #1a1f2a 0%, #0a0e14 60%, #050709 100%)'
  },
  frame: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    border: 0,
    display: 'block',
    background: 'transparent'
  }
};

export default Template19MrLiquid;
